import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import clsx from 'clsx';
import { CheckBadgeIcon, ClockIcon } from '@heroicons/react/24/outline';
import type { FieldReport } from '../../store/reportStore';
import { REPORT_TYPE_META, PRIORITY_META, payloadRows, minutesSince } from '../../lib/reports';
import AttachmentViewer from './AttachmentViewer';

function safeTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : format(d, 'HH:mm:ss · dd MMM');
}

// Live-updating chip: minutes since the report was received. Amber past 5 min,
// red past 15 min while still unacknowledged — an ACK SLA at a glance.
function SlaChip({ report }: { report: FieldReport }) {
  const [, force] = useState(0);
  useEffect(() => {
    if (report.status === 'ACKED') return;
    const t = setInterval(() => force((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [report.status]);

  const mins = minutesSince(report.createdAt);
  const tone =
    report.status === 'ACKED'
      ? 'text-gray-500 border-command-border'
      : mins >= 15
        ? 'text-severity-critical border-severity-critical/50 animate-pulse'
        : mins >= 5
          ? 'text-severity-medium border-severity-medium/50'
          : 'text-gray-400 border-command-border';

  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-tactical', tone)}>
      <ClockIcon className="w-3 h-3" />
      {report.status === 'ACKED' ? `ACK ${safeTime(report.ackedAt)}` : `+${mins}m`}
    </span>
  );
}

interface Props {
  report: FieldReport;
  onAck: (id: string) => void;
  acking?: boolean;
  compact?: boolean;
}

export default function ReportCard({ report, onAck, acking, compact }: Props) {
  const typeMeta = REPORT_TYPE_META[report.type] ?? { label: report.type, icon: '📄' };
  const prio = PRIORITY_META[report.priority] ?? PRIORITY_META.ROUTINE;
  const rows = payloadRows(report.type, report.payload);
  const isUnacked = report.status !== 'ACKED';
  const needsAck = report.ackRequired && isUnacked;
  const callsign = report.platoon?.callsign ?? 'Unknown';

  return (
    <div
      className={clsx(
        'rounded-lg border bg-command-bg p-3 transition-colors',
        prio.border,
        needsAck && (report.priority === 'FLASH' || report.priority === 'IMMEDIATE') && 'bg-severity-critical/[0.04]'
      )}
    >
      <div className="flex items-start gap-2">
        <span className="text-lg leading-none mt-0.5" aria-hidden>{typeMeta.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-100">{typeMeta.label}</span>
            <span className={clsx('px-1.5 py-0.5 rounded text-[10px] font-bold font-tactical', prio.badge)}>
              {prio.label}
            </span>
            <span className="text-xs text-gray-400">{callsign}</span>
            <span className="ml-auto flex items-center gap-2">
              <SlaChip report={report} />
              <span className="text-[10px] text-gray-500 font-tactical">{safeTime(report.createdAt)}</span>
            </span>
          </div>

          {rows.length > 0 && (
            <dl className={clsx('mt-2 grid gap-x-4 gap-y-1', compact ? 'grid-cols-1' : 'sm:grid-cols-2')}>
              {rows.map((r) => (
                <div key={r.label} className="text-xs">
                  <dt className="text-gray-500 font-tactical inline">{r.label}: </dt>
                  <dd className="text-gray-200 inline">{r.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {(report.mgrs || (typeof report.lat === 'number' && typeof report.lon === 'number')) && (
            <div className="mt-1 text-[10px] text-gray-500 font-tactical">
              {report.mgrs
                ? report.mgrs
                : `${(report.lat as number).toFixed(5)}, ${(report.lon as number).toFixed(5)}`}
            </div>
          )}

          {!compact && <AttachmentViewer attachments={report.attachments} />}

          <div className="mt-2 flex items-center gap-2">
            {report.status === 'ACKED' ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-command-success">
                <CheckBadgeIcon className="w-4 h-4" /> Acknowledged
              </span>
            ) : needsAck ? (
              <button
                type="button"
                onClick={() => onAck(report.id)}
                disabled={acking}
                className="px-3 py-1.5 rounded-md bg-command-accent text-command-bg text-xs font-bold hover:bg-command-accent/90 transition-colors disabled:opacity-50"
              >
                {acking ? 'ACKNOWLEDGING…' : 'ACKNOWLEDGE'}
              </button>
            ) : (
              <span className="text-[11px] text-gray-500">No ACK required</span>
            )}
            {report.attachments.length > 0 && compact && (
              <span className="text-[10px] text-gray-500">{report.attachments.length} attachment(s)</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
